import QueryTextWithHover from "@/pages/query/QueryText";
import { formatCost, formatMilliseconds, getInstanceName } from "@/helpers/utils";
import { TableQuery } from "../types";
import { ColorInsightTags } from "@/components/tags";
import dayjs from "dayjs";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { getCurrencySymbol } from "@uicore";
import { Datastore } from "@/pages/datastores/types";

interface GetTableQueriesColumnsParams {
  getOptions: (data?: string[]) => { label: string; value: string }[];
  filters?: {
    insights?: string[];
    access_types?: string[];
    warehouses?: string[];
    users?: string[];
  };
  currency: string;
  datastores: Datastore[];
}

export const getTableQueriesColumns = ({
  getOptions,
  filters,
  currency,
  datastores,
}: GetTableQueriesColumnsParams): TableHeaderType<TableQuery>[] => [
  {
    id: 1,
    label: "Query Text",
    uid: "query_text",
    width: "10%",
    render: (item: TableQuery) => (
      <QueryTextWithHover queryText={item.query_text || ""} queryHash={item.hash} />
    ),
  },
  {
    id: 2,
    label: "Est. Cost",
    uid: "estimated_cost",
    filter: {
      filterType: FilterType.TEXT,
      placeHolder: `Specify cost in ${getCurrencySymbol(currency)}`,
      label: "Cost",
      searchKey: "estimatedCost",
    },
    sort: {
      sortSearchKey: "sortAttribute",

      sortParamValue: "query_cost",
    },
    width: "5%",
    render: (item: TableQuery) => <span>{formatCost(item.query_cost, currency)}</span>,
  },
  {
    id: 3,
    label: "Exec. Time",
    uid: "execution_time",
    filter: {
      filterType: FilterType.TEXT,
      placeHolder: `Specify time in minutes`,
      label: "Execution Time",
      searchKey: "executionTime",
    },
    sort: {
      sortSearchKey: "sortAttribute",

      sortParamValue: "execution_time",
    },
    width: "7%",
    render: (item: TableQuery) => <span>{formatMilliseconds(item.execution_time)}</span>,
  },
  {
    id: 4,
    label: "Insights",
    uid: "insights",
    filter: {
      filterType: FilterType.DROPDOWN,
      options: getOptions(filters?.insights),
      searchKey: "insights",
    },
    width: "8%",
    render: (item: TableQuery) =>
      item.insights && item.insights.length > 0 ? (
        <ColorInsightTags tags={item.insights || []} showMore />
      ) : (
        <span>NA</span>
      ),
  },
  {
    id: 5,
    label: "Timestamp",
    uid: "timestamp",
    width: "7%",
    sort: {
      sortSearchKey: "sortAttribute",

      sortParamValue: "end_time",
    },
    render: (item: TableQuery) => <span>{dayjs(item.end_time).format("DD-MM-YYYY hh:mm:ss")}</span>,
  },
  {
    id: 6,
    label: "Access Type",
    uid: "access_type",
    width: "5%",
    filter: {
      filterType: FilterType.DROPDOWN,
      options: getOptions(filters?.access_types),
      searchKey: "accessType",
    },
    render: (item: TableQuery) => <span>{item.access_type || "NA"}</span>,
  },
  {
    id: 7,
    label: "Warehouse",
    uid: "warehouse",
    width: "8%",
    filter: {
      filterType: FilterType.DROPDOWN,
      options: getOptions(filters?.warehouses),
      searchKey: "warehouse",
    },
    render: (item: TableQuery) => <span>{item.warehouse_name}</span>,
  },
  {
    id: 8,
    label: "User",
    uid: "user",
    width: "8%",
    filter: {
      filterType: FilterType.DROPDOWN,
      options: getOptions(filters?.users),
      searchKey: "user",
    },
    render: (item: TableQuery) => <span>{item.user_name}</span>,
  },
  {
    id: 9,
    label: "Instance",
    uid: "instance",
    width: "5%",
    filter: {
      filterType: FilterType.DROPDOWN,
      searchKey: "instance",
      options: (datastores || []).map(instance => ({
        label: instance.name,
        value: (instance.instance_id || "").toString(),
      })),
    },
    render: (item: TableQuery) => (
      <span>{item.instance_id ? getInstanceName(item.instance_id, datastores || []) : "N/A"}</span>
    ),
  },
  {
    id: 10,
    label: "Query Hash",
    uid: "hash",
    width: "10%",
    render: (item: TableQuery) => <span>{item.hash}</span>,
  },
];
